import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Edit3, Loader2, MessageSquare, X, CheckCircle2, AlertCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import RequestTransferModificationModal from './RequestTransferModificationModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGES = {
  pending:   { label: 'En attente', cls: 'bg-amber-100 text-amber-800 border-amber-200', icon: Clock },
  approved:  { label: 'Approuvée', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle2 },
  rejected:  { label: 'Refusée',   cls: 'bg-red-100 text-red-800 border-red-200', icon: AlertCircle },
  cancelled: { label: 'Annulée',   cls: 'bg-slate-100 text-slate-600 border-slate-200', icon: X },
};

/**
 * Section "Demandes de modification" (Phase 3a — PM Spec 07/05/2026)
 *
 * Props:
 *   contractId, runId, run
 *   canRequest: bool (false si transfert terminé)
 */
const TransferModificationRequestsSection = ({ contractId, runId, run, canRequest = true }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);

  const fetchItems = useCallback(async () => {
    if (!contractId || !runId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/client/contracts/${contractId}/transfers/${runId}/modification-requests`,
        {},
      );
      setItems(res.data?.items || []);
    } catch (e) {
      console.error('Error fetching modification requests', e);
    } finally {
      setLoading(false);
    }
  }, [contractId, runId]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const cancelRequest = async (id) => {
    if (!window.confirm('Annuler cette demande ?')) return;
    setCancellingId(id);
    try {
      await axios.post(`${API}/client/modification-requests/${id}/cancel`, {},
        {});
      toast.success('Demande annulée');
      fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de l\'annulation');
    } finally {
      setCancellingId(null);
    }
  };

  const renderProposedSummary = (changes = {}) => {
    const parts = [];
    if (changes.departure?.address) parts.push(`Départ → ${changes.departure.address}`);
    if (changes.arrival?.address) parts.push(`Arrivée → ${changes.arrival.address}`);
    if (changes.merchandiseDescription) parts.push(`Marchandise → ${changes.merchandiseDescription}`);
    if (changes.weight !== undefined && changes.weight !== null) parts.push(`Poids → ${changes.weight} t`);
    if (changes.vehicleType) parts.push(`Véhicule → ${changes.vehicleType}`);
    return parts;
  };

  return (
    <>
      <Card data-testid="modification-requests-section">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Edit3 className="w-4 h-4 text-emerald-600" />
            Demandes de modification ({items.length})
          </CardTitle>
          {canRequest && (
            <Button
              size="sm"
              onClick={() => setModalOpen(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="open-modif-request-btn"
            >
              <Edit3 className="w-3.5 h-3.5 mr-1.5" />
              Nouvelle demande
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-emerald-600" />
            </div>
          ) : items.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">
              Aucune demande pour le moment.
              {canRequest && ' Cliquez sur "Nouvelle demande" pour proposer un changement.'}
            </p>
          ) : (
            <div className="space-y-3">
              {items.map((req) => {
                const meta = STATUS_BADGES[req.status] || STATUS_BADGES.pending;
                const Icon = meta.icon;
                const summary = renderProposedSummary(req.proposedChanges);
                return (
                  <div key={req.id} className="border rounded-lg p-3 space-y-2"
                    data-testid={`modif-request-${req.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className={`text-xs ${meta.cls}`}>
                          <Icon className="w-3 h-3 mr-1" />
                          {meta.label}
                        </Badge>
                        <span className="text-xs text-slate-500">
                          {new Date(req.createdAt).toLocaleString('fr-FR')}
                        </span>
                      </div>
                      {req.status === 'pending' && (
                        <Button
                          size="sm" variant="ghost"
                          className="h-7 text-xs text-red-600 hover:bg-red-50"
                          onClick={() => cancelRequest(req.id)}
                          disabled={cancellingId === req.id}
                          data-testid={`cancel-modif-${req.id}`}
                        >
                          {cancellingId === req.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Annuler'}
                        </Button>
                      )}
                    </div>

                    {summary.length > 0 && (
                      <ul className="text-xs text-slate-700 space-y-0.5 ml-1">
                        {summary.map((s, i) => <li key={i}>• {s}</li>)}
                      </ul>
                    )}

                    {req.proposedChanges?.comment && (
                      <div className="flex items-start gap-1.5 bg-slate-50 rounded p-2">
                        <MessageSquare className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
                        <p className="text-xs text-slate-600 italic">{req.proposedChanges.comment}</p>
                      </div>
                    )}

                    {req.adminResponse && (
                      <div className="bg-blue-50 border-l-2 border-blue-300 rounded p-2">
                        <p className="text-xs font-medium text-blue-900 mb-0.5">Réponse de notre équipe :</p>
                        <p className="text-xs text-blue-800">{req.adminResponse}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <RequestTransferModificationModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        contractId={contractId}
        runId={runId}
        run={run}
        onSuccess={fetchItems}
      />
    </>
  );
};

export default TransferModificationRequestsSection;
